import { Core } from "@faire/design-tokens";
import { useStrictLocalization } from "@faire/web--source/common/localization";
import { Add } from "@faire/web--source/slate/icons/foundation/Add";
import { Row } from "@faire/web--source/slate/Layout";
import { getSpacing, Spacer } from "@faire/web--source/slate/spacing";
import * as React from "react";

import { CreateNewOptionDropdownItem } from "../styles";
import { ICreateNewOption } from "../types";

import { IOptionMixin } from "./types";

interface CreateNewOptionButtonProps<T extends string> {
  createNewOption?: ICreateNewOption;
  searchValue?: string;
  filteredOptions: IOptionMixin<T>[];
  highlightIndex: number;
  onHighlight: (index: number) => void;
  closeDropdown: () => void;
  resetSearchValue: () => void;
  refs: React.MutableRefObject<Record<number, HTMLButtonElement | null>>;
}

export const CreateNewOptionButton = <T extends string>({
  createNewOption,
  searchValue,
  filteredOptions,
  highlightIndex,
  onHighlight,
  closeDropdown,
  resetSearchValue,
  refs,
}: CreateNewOptionButtonProps<T>) => {
  const { strictLocalize } = useStrictLocalization();

  const handleClick = React.useCallback(() => {
    if (createNewOption?.closeDropdown) {
      closeDropdown();
    }

    if (createNewOption?.hideUnlessNoOptions) {
      resetSearchValue();
    }

    createNewOption?.onClick?.(searchValue);
  }, [createNewOption, closeDropdown, resetSearchValue, searchValue]);

  if (!createNewOption) {
    return null;
  }

  if (
    createNewOption.hideUnlessNoOptions &&
    (!searchValue ||
      filteredOptions.some(
        (option) =>
          option.label.toLocaleLowerCase() ===
          searchValue.toLocaleLowerCase().trim()
      ))
  ) {
    return null;
  }

  const index = filteredOptions.length + 1;
  const createNewOptionLabel =
    typeof createNewOption.label === "function"
      ? createNewOption.label(searchValue)
      : createNewOption.label;

  return (
    <CreateNewOptionDropdownItem
      key="dropdownItem_CustomizedOption"
      role="option"
      tabIndex={-1}
      onClick={handleClick}
      data-test-id="dropdownItem_CustomizedOption"
      type="button"
      ref={(el: HTMLButtonElement | null) => {
        if (!el) {
          return;
        }
        refs.current[index] = el;
      }}
      onMouseOver={() => onHighlight(index)}
      $highlight={index === highlightIndex}
    >
      <Row align="center">
        <Add
          color={Core.icon.default}
          height={getSpacing("2x")}
          width={getSpacing("2x")}
          aria-label={strictLocalize({
            defaultMessage: "Add new option value",
            description: {
              text: "Message for screen readers when selecting a customized option value",
            },
          })}
        />
        <Spacer size="1x" />
        {createNewOptionLabel}
      </Row>
    </CreateNewOptionDropdownItem>
  );
};
